import cx from 'classnames';

import { useTrackEventAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { Button, ButtonProps } from '../../../../../design-system/components/form/button/Button';
import { BrandHandMarkTwoLinesVariant2 } from '../../../../../design-system/components/illustration/BrandHandMarkTwoLinesVariant2';
import { Typography } from '../../../../../design-system/components/typography/Typography';
import { ActionBlockSliceType } from '../../../../../domains/Prismic/BrandPages/BrandPagesDocument';
import { EvaneosPrismicRichText } from '../../../../../domains/Prismic/EvaneosPrismicRichText';

import style from './ActionBlockSlice.module.css';

export type ActionBlockSliceProps = ActionBlockSliceType;

export function ActionBlockSlice({
    version,
    title,
    description,
    buttonType,
    subtitle,
    ctaLabel,
    ctaLink,
    sliceIndex,
    target,
}: ActionBlockSliceProps & { sliceIndex?: number }) {
    const isLightBackground = version === 'yellow' || version === 'white';
    const { trackEvent } = useTrackEventAmplitude();

    return (
        <div className={style.wrapper}>
            <div className={style.titleWrapper}>
                <Typography as="h2" scale="secondary-m-bold">
                    {title}
                </Typography>
                <BrandHandMarkTwoLinesVariant2
                    color={
                        isLightBackground
                            ? 'var(--color-primary-main)'
                            : 'var(--color-secondary-main)'
                    }
                />
            </div>

            {subtitle ? (
                <Typography
                    as="div"
                    scale="primary-m-extra-bold"
                    className={cx({
                        ['textYellow']: version === 'green',
                    })}
                >
                    {subtitle}
                </Typography>
            ) : null}

            <EvaneosPrismicRichText field={description} />

            {ctaLabel && ctaLink?.url ? (
                <div>
                    <Button
                        priority={getButtonPriority(version, buttonType)}
                        reversed={version === 'green'}
                        href={ctaLink?.url}
                        target={target || undefined}
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.actionBlockClicked({
                                    ...defaultProperties,
                                    block_index: sliceIndex ? sliceIndex + 1 : 0,
                                    block_title: title ?? '',
                                    click_type: 'button',
                                });
                            });
                        }}
                    >
                        {ctaLabel}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}

function getButtonPriority(
    version: ActionBlockSliceProps['version'],
    buttonType: ActionBlockSliceProps['buttonType'],
): ButtonProps['priority'] {
    if ((version === 'green' || version === 'white') && buttonType === 'filled') {
        return 'primary';
    }
    if (
        (version === 'green' || version === 'white' || version === 'yellow') &&
        buttonType === 'outlined'
    ) {
        return 'secondary';
    }

    if (version === 'yellow' && buttonType === 'filled') {
        return 'secondary-filled';
    }
    if (
        (version === 'green' || version === 'white' || version === 'yellow') &&
        buttonType === 'text'
    ) {
        return 'text';
    }

    return 'primary';
}
